import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Radio, Activity, Bell, ArrowRight, Loader2, Zap, Target, Eye, Gauge, TrendingDown } from 'lucide-react';
import { apiGet } from '../data/api';
import t from '../i18n/it';

function StatCard({ icon: Icon, value, label, color }) {
  const colors = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
  };
  return (
    <div
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
      className="rounded-sm p-5 fade-in"
      style={{ background: '#18181B', border: '1px solid #27272A' }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
          {label}
        </span>
        <Icon size={17} className={colors[color]} />
      </div>
      <div
        className="text-3xl font-bold text-zinc-50"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {value ?? '—'}
      </div>
    </div>
  );
}

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">
      {t.common.tennis}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">
      {t.common.table_tennis}
    </span>
  );
}

function EvBadge({ ev }) {
  const pct = (ev * 100).toFixed(1);
  if (ev >= 0.10)
    return (
      <span className="px-1.5 py-0.5 text-xs rounded bg-emerald-500/15 text-emerald-400">
        +{pct}%
      </span>
    );
  if (ev >= 0.05)
    return (
      <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/15 text-amber-400">
        +{pct}%
      </span>
    );
  return (
    <span className="px-1.5 py-0.5 text-xs rounded bg-zinc-500/15 text-zinc-400">
      +{pct}%
    </span>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet('/dashboard/stats')
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
        <Loader2 size={18} className="animate-spin" />
        <span>{t.common.loading}</span>
      </div>
    );

  if (error)
    return (
      <div className="text-red-400 py-16 text-center">
        {t.common.error}: {error}
      </div>
    );

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div>
        <h1
          className="text-4xl font-bold text-zinc-50 tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t.dashboard.title}
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.dashboard.subtitle}</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={TrendingUp}
          value={stats?.prematch_signals_today}
          label={t.dashboard.prematch_today}
          color="emerald"
        />
        <StatCard
          icon={Radio}
          value={stats?.live_signals_today}
          label={t.dashboard.live_today}
          color="blue"
        />
        <StatCard
          icon={Activity}
          value={stats?.live_matches}
          label={t.dashboard.live_matches}
          color="amber"
        />
        <StatCard
          icon={Bell}
          value={stats?.alerts_today}
          label={t.dashboard.alerts_today}
          color="rose"
        />
      </div>

      {/* Prematch engine cards */}
      <div>
        <h2 className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-500 mb-3 px-1">
          {t.dashboard.prematch_engine}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Best EV prematch */}
          <div
            data-testid="engine-best-ev-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-emerald-500/10">
                <Zap size={14} className="text-emerald-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.best_ev_today}
              </span>
            </div>
            {stats?.engine_best_ev ? (
              <>
                <div
                  className="text-2xl font-bold text-emerald-400 mb-1"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  +{(stats.engine_best_ev.expected_value * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-zinc-300 font-medium">
                  {stats.engine_best_ev.selection_name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {stats.engine_best_ev.match_name}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  @ {stats.engine_best_ev.bookmaker_odds?.toFixed(2)} ({stats.engine_best_ev.bookmaker_name})
                </div>
              </>
            ) : (
              <div className="text-zinc-600 text-sm">{t.dashboard.no_engine_signal}</div>
            )}
          </div>

          {/* Max confidence */}
          <div
            data-testid="engine-max-confidence-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-blue-500/10">
                <Target size={14} className="text-blue-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.max_confidence}
              </span>
            </div>
            {stats?.engine_max_confidence ? (
              <>
                <div
                  className="text-2xl font-bold text-blue-400 mb-1"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {(stats.engine_max_confidence.confidence_score * 100).toFixed(0)}%
                </div>
                <div className="text-sm text-zinc-300 font-medium">
                  {stats.engine_max_confidence.selection_name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {stats.engine_max_confidence.match_name}
                </div>
              </>
            ) : (
              <div className="text-zinc-600 text-sm">{t.dashboard.no_engine_signal}</div>
            )}
          </div>

          {/* Watch live */}
          <div
            data-testid="engine-watch-live-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-amber-500/10">
                <Eye size={14} className="text-amber-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.watch_live}
              </span>
            </div>
            <div
              className="text-2xl font-bold text-amber-400 mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {stats?.engine_watch_live_count ?? 0}
            </div>
            {stats?.engine_watch_live_matches?.length > 0 ? (
              <div className="space-y-1.5">
                {stats.engine_watch_live_matches.map((m) => (
                  <Link
                    key={m.match_id}
                    to={`/incontri/${m.match_id}`}
                    data-testid={`watch-live-link-${m.match_id}`}
                    className="flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors"
                  >
                    <SportBadge sport={m.sport} />
                    <span className="truncate">{m.match_name}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-zinc-600 text-xs">{t.dashboard.no_match_to_follow}</div>
            )}
          </div>
        </div>
      </div>

      {/* Live engine cards */}
      <div>
        <h2 className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-500 mb-3 px-1 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" />
          {t.dashboard.live_engine}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Live signals count */}
          <div
            data-testid="live-engine-signals-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-red-500/10">
                <Radio size={14} className="text-red-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.live_signals_active}
              </span>
            </div>
            <div
              className="text-2xl font-bold text-red-400 mb-1"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {stats?.live_engine_signals_count ?? 0}
            </div>
            <div className="text-xs text-zinc-600">
              {t.common.on} {stats?.live_matches ?? 0} match live
            </div>
          </div>

          {/* Best EV live */}
          <div
            data-testid="live-engine-best-ev-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-emerald-500/10">
                <Zap size={14} className="text-emerald-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.best_ev_live}
              </span>
            </div>
            {stats?.live_engine_best_ev ? (
              <>
                <div
                  className="text-2xl font-bold text-emerald-400 mb-1"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  +{(stats.live_engine_best_ev.expected_value * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-zinc-300 font-medium">
                  {stats.live_engine_best_ev.selection_name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {stats.live_engine_best_ev.match_name}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  @ {stats.live_engine_best_ev.live_odds?.toFixed(2)} ({stats.live_engine_best_ev.bookmaker_name})
                </div>
              </>
            ) : (
              <div className="text-zinc-600 text-sm">{t.dashboard.no_live_signal}</div>
            )}
          </div>

          {/* Strongest momentum */}
          <div
            data-testid="live-engine-momentum-card"
            className="rounded-sm p-5"
            style={{ background: '#18181B', border: '1px solid #27272A' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-purple-500/10">
                <Gauge size={14} className="text-purple-400" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-400">
                {t.dashboard.strongest_momentum}
              </span>
            </div>
            {stats?.live_engine_strongest_momentum ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {stats.live_engine_strongest_momentum.momentum_score > 0 ? (
                    <TrendingUp size={18} className="text-emerald-400" />
                  ) : (
                    <TrendingDown size={18} className="text-red-400" />
                  )}
                  <span
                    className={`text-2xl font-bold ${
                      stats.live_engine_strongest_momentum.momentum_score > 0
                        ? 'text-emerald-400'
                        : 'text-red-400'
                    }`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {stats.live_engine_strongest_momentum.momentum_score > 0 ? '+' : ''}
                    {stats.live_engine_strongest_momentum.momentum_score.toFixed(0)}
                  </span>
                </div>
                <div className="text-sm text-zinc-300 font-medium">
                  {stats.live_engine_strongest_momentum.selection_name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {stats.live_engine_strongest_momentum.match_name}
                </div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  {t.signals.pressure}: {stats.live_engine_strongest_momentum.pressure_score.toFixed(0)}
                </div>
              </>
            ) : (
              <div className="text-zinc-600 text-sm">{t.dashboard.no_momentum}</div>
            )}
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top opportunities */}
        <div
          className="lg:col-span-2 rounded-sm"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: '1px solid #27272A' }}
          >
            <h2
              className="font-semibold text-zinc-200"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t.dashboard.top_opportunities}
            </h2>
            <Link
              to="/segnali-prematch"
              data-testid="dashboard-all-signals-link"
              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors"
            >
              {t.dashboard.all_signals} <ArrowRight size={12} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #27272A' }}>
                  {[t.dashboard.th_match, t.dashboard.th_sport, t.dashboard.th_signal, t.dashboard.th_bookmaker, t.dashboard.th_odds, t.dashboard.th_ev].map((h, i) => (
                    <th
                      key={h}
                      className={`py-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-medium ${
                        i === 0 ? 'text-left px-5' : i >= 4 ? 'text-right px-4' : 'text-left px-4'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats?.top_opportunities?.length > 0 ? (
                  stats.top_opportunities.map((sig) => (
                    <tr
                      key={sig.id}
                      data-testid={`top-signal-${sig.id}`}
                      className="transition-colors"
                      style={{ borderBottom: '1px solid #1F1F22' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(39,39,42,0.5)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td className="px-5 py-3 text-zinc-200 font-medium text-sm">
                        <Link to={`/incontri/${sig.match_id}`} className="hover:text-emerald-400 transition-colors">
                          {sig.match_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <SportBadge sport={sig.sport} />
                      </td>
                      <td className="px-4 py-3 text-zinc-300 text-sm">{sig.selection_name}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">{sig.bookmaker_name}</td>
                      <td
                        className="px-4 py-3 text-right text-emerald-400"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {sig.bookmaker_odds?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <EvBadge ev={sig.expected_value} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-zinc-600 text-sm">
                      {t.dashboard.no_opportunities}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Live matches */}
        <div
          className="rounded-sm"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: '1px solid #27272A' }}
          >
            <h2
              className="font-semibold text-zinc-200 flex items-center gap-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="w-2 h-2 rounded-full bg-red-500 live-dot" />
              {t.dashboard.recent_live}
            </h2>
            <Link
              to="/incontri?status=live"
              data-testid="dashboard-all-matches-link"
              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors"
            >
              {t.dashboard.all_matches} <ArrowRight size={12} />
            </Link>
          </div>
          <div>
            {stats?.live_matches_list?.length > 0 ? (
              stats.live_matches_list.map((match) => (
                <Link
                  key={match.id}
                  to={`/incontri/${match.id}`}
                  data-testid={`live-match-${match.id}`}
                  className="block px-5 py-3.5 transition-colors"
                  style={{ borderBottom: '1px solid #1F1F22' }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'rgba(39,39,42,0.5)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <SportBadge sport={match.sport} />
                    <span
                      className="text-xs text-red-400 font-medium"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      LIVE
                    </span>
                  </div>
                  <div className="text-sm font-medium text-zinc-200">
                    {match.player1_name}{' '}
                    <span className="text-zinc-600">vs</span>{' '}
                    {match.player2_name}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">{match.tournament_name}</div>
                </Link>
              ))
            ) : (
              <div className="px-5 py-10 text-center text-zinc-600 text-sm">
                {t.dashboard.no_live_matches}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
